// src\components\VideoEngine\Common\TeaserCarousel.js

// This component creates the Intro's rapid-fire image cycle that ends on a "Mystery Card." 
// It includes the Delay Logic to ensure the animation pauses while the user is "inhaling" (waiting for audio).

import React from "react";
import { AbsoluteFill, Img, useCurrentFrame, interpolate } from "remotion";

export const TeaserCarousel = ({
  images = [],
  duration,
  theme,
  delay = 0, // CRITICAL: The "Inhale" delay
  badgePosition = "bottom-right", // ARCHITECT INCISION: Safe default for legacy MSN templates
}) => {
  const frame = useCurrentFrame();

  // 1. CALCULATE TIMING (Corrected for Audio Delay)
  // We only care about time *after* the delay.
  // If the delay is 60f and total duration is 300f, the carousel actually runs for 240f.
  const effectiveDuration = Math.max(1, duration - delay);
  
  // We shift the current frame back by the delay amount.
  // If we are at frame 60 (start of audio), effectiveFrame is 0.
  const effectiveFrame = Math.max(0, frame - delay);

  // We divide the *effective* duration by the number of slots (Images + 1 Mystery Card)
  const totalSlots = images.length + 1;
  const framesPerSlot = effectiveDuration / totalSlots;
  
  // Which slot are we currently in?
  const currentSlotIndex = Math.floor(effectiveFrame / framesPerSlot);
  
  // 2. DETERMINE CONTENT
  const isMystery = currentSlotIndex >= images.length;
  // Safety check to prevent index out of bounds
  const currentImage = images[Math.min(currentSlotIndex, images.length - 1)] || "";

  // 3. CONTINUOUS GLOBAL ZOOM
  // We zoom smoothly from 1.15 down to 1.0 over the ENTIRE duration.
  // This decouples the camera move from the edit points so cuts feel "embedded" in the movement.
  const zoom = interpolate(effectiveFrame, [0, effectiveDuration], [1.15, 1.0], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp"
  });

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "20px",
        overflow: "hidden",
        position: "relative",
        boxShadow: "0 20px 50px rgba(0,0,0,0.5)", // Deep shadow for depth
        border: `4px solid ${theme.secondary}`,    // Brand Frame
        backgroundColor: "#000",
      }}
    >
      {isMystery ? (
        // A. MYSTERY CARD (The "Question Mark")
        <AbsoluteFill
          style={{
            backgroundColor: theme.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <h1
            style={{
              fontSize: 300,
              color: "white",
              fontFamily: theme.fontMain,
              margin: 0,
              lineHeight: 0.8,
              opacity: 0.5,
              transform: `scale(${zoom})`, 
              transformOrigin: "center center",
            }}
          >
            ?
          </h1>
          <h3 style={{ 
              color: theme.primary, 
              fontFamily: theme.fontMain, 
              marginTop: 10,
              fontSize: 60,
              letterSpacing: 2,
              // Parallax: Move text slightly opposite to the ? for depth
              transform: `translateY(${(zoom - 1) * 100}px)` 
            }}>
             TOP SPOT
          </h3>
        </AbsoluteFill>
      ) : (
        // B. IMAGE TEASER (The Rapid Fire)
        <AbsoluteFill>
          <Img
            src={currentImage}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transform: `scale(${zoom})`, // Continuous gentle zoom
            }}
          />
          {/* Label Badge */}
          <div style={{
            position: 'absolute',
            ...(badgePosition === 'top-right' 
                ? { top: 20, right: 20 } 
                : { bottom: 20, right: 20 }
            ),
            background: "rgba(0,0,0,0.7)",
            color: "white",
            padding: "5px 15px",
            borderRadius: "10px",
            fontFamily: theme.fontMain,
            fontSize: 40
          }}>
             #{images.length - currentSlotIndex + 1}
          </div>
        </AbsoluteFill>
      )}
    </div>
  );
};